import { getSBLAll, SBLActiveCases, SBLRejected, SBLSanction,SBLCancelled,SBLUnserviceable,SBLContinueJourneyCases } from "./loanCodes/LAP";
import { UBLActiveCases, UBLSanction, UBLRejected, getUBLAll ,UBLCancelled,UBLUnserviceable,UBLContinueJourneyCases} from "./loanCodes/UBL";
import { SHLActiveCases, SHLSanction, SHLRejected, getSHLAll, SHLCancelled, SHLUnserviceable,SHLContinueJourneyCases } from "./loanCodes/HL";
import { UPLActiveCases, UPLSanction, UPLRejected, getUPLAll,UPLCancelled,UPLUnserviceable,UPLContinueJourneyCases } from "./loanCodes/PL";
import { SGLActiveCases, SGLSanction, SGLRejected, getSGLAll , SGLCancelled,SGLUnserviceable,SGLContinueJourneyCases} from "./loanCodes/GL";
import { NAActiveCases } from "./loanCodes/NA";
import { deriveStatusFamily } from "./loanCodes/derive";

// Balance Transfer, Plot Loan and Other were seeded by cloning LAP's
// application status codes, so their dashboard content is LAP's with the code
// prefix rewritten. Deriving it keeps one copy: three hand-written files would
// drift apart the first time anyone edited the shared copy. See
// loanCodes/derive.js for why this does not generalise to UBL, PL or GL.
const LAP_FAMILY = {
    in_progress: SBLActiveCases,
    submitted: SBLSanction,
    rejected_loans: SBLRejected,
    cancelled_loans: SBLCancelled,
    unserviceable_loans: SBLUnserviceable,
    continue_journey: SBLContinueJourneyCases,
    all: getSBLAll,
};

const DERIVED_FROM_LAP = ['BT', 'PLT', 'OTH'];

const derivedFamilies = Object.fromEntries(
    DERIVED_FROM_LAP.map((code) => [
        code,
        deriveStatusFamily(LAP_FAMILY, 'LAP', code),
    ])
);

// Label only: per-loan-type colour was dropped, and the one place that read it
// already fell back to a neutral grey. Per-STATUS colour is unrelated and still
// lives in loanCodes/*.js.
export const loanTypeMap = {
    'LAP': { "label": "loan_types.property" },
    'UBL': { "label": "loan_types.unsecured_business" },
    'HL': { "label": "loan_types.home" },
    'PL': { "label": "loan_types.personal" },
    'BT': { "label": "loan_types.balance_transfer" },
    'PLT': { "label": "loan_types.plot" },
    'OTH': { "label": "loan_types.other" },
    // Not a product: the bucket for an enquiry with no loan type yet. No API
    // row describes it, so it stays declared here.
    'null': { "label": "loan_types.null" },
};

const derivedKeys = (group) =>
    Object.values(derivedFamilies).flatMap((family) => Object.keys(family[group]));

export const continueJourneyStatusCodes = [
    ...derivedKeys('continue_journey'),
    ...Object.keys(SBLContinueJourneyCases),
    ...Object.keys(UBLContinueJourneyCases),
    ...Object.keys(SHLContinueJourneyCases),
    ...Object.keys(UPLContinueJourneyCases),
    ...Object.keys(SGLContinueJourneyCases),
];

// The codes an application reaches once it is with Optimo. The approved code
// is listed here rather than taken from the *Sanction dictionaries: those no
// longer carry it (the dashboard stopped showing an "Application Approved"
// step), but support's "View Application" button must still appear for a lead
// sitting on it.
export const approvedStatusCodes = [
    'OPTLAP10',
    'OPTPL10',
    'OPTHL10',
    'OPTGL08',
    ...DERIVED_FROM_LAP.map((code) => `OPT${code}10`),
];

export const viewApplicationStatusCodes = [
    ...derivedKeys('submitted'),
    ...Object.keys(SBLSanction),
    ...Object.keys(UBLSanction),
    ...Object.keys(SHLSanction),
    ...Object.keys(UPLSanction),
    ...Object.keys(SGLSanction),
    ...approvedStatusCodes,
];

export const showContinueJourneyStatusCodes = [
    ...derivedKeys('in_progress'),
    ...Object.keys(SBLActiveCases),
    ...Object.keys(UBLActiveCases),
    ...Object.keys(SHLActiveCases),
    ...Object.keys(UPLActiveCases),
    ...Object.keys(SGLActiveCases),
];

export const resellerStatusPerLoanType = {
    "LAP": {
        "in_progress": SBLActiveCases,
        "submitted": SBLSanction,
        "rejected_loans": SBLRejected,
        "cancelled_loans": SBLCancelled,
        "unserviceable_loans": SBLUnserviceable,
        "all": getSBLAll,
    },
    "UBL": {
        "in_progress": UBLActiveCases,
        "submitted": UBLSanction,
        "rejected_loans": UBLRejected,
        "cancelled_loans": UBLCancelled,
        "unserviceable_loans": UBLUnserviceable,
        "all": getUBLAll
    },
    "HL": {
        "in_progress": SHLActiveCases,
        "submitted": SHLSanction,
        "rejected_loans": SHLRejected,
        "cancelled_loans": SHLCancelled,
        "unserviceable_loans": SHLUnserviceable,
        "all": getSHLAll

    },
    "PL": {
        "in_progress": UPLActiveCases,
        "submitted": UPLSanction,
        "rejected_loans": UPLRejected,
        "cancelled_loans": UPLCancelled,
        "unserviceable_loans": UPLUnserviceable,
        "all": getUPLAll
    },
    "GL": {
        "in_progress": SGLActiveCases,
        "submitted": SGLSanction,
        "rejected_loans": SGLRejected,
        "cancelled_loans": SGLCancelled,
        "unserviceable_loans": SGLUnserviceable,
        "all": getSGLAll
    },
    ...derivedFamilies,
    "null": {
        "in_progress": NAActiveCases
    }
}

export const formatDate = (dateString) => {
    if (!dateString){
        return null
    }
    return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

// const OPTIMO01 = {
//     header: {
//         en: 'Chat Initiated',
//         hi: 'चैट प्रारंभ',
//         kn: 'ಚಾಟ್ ಆರಂಭಿಸಲಾಗಿದೆ',
//         ta: 'உரையாடல் தொடங்கப்பட்டது',
//         te: 'చాట్ ప్రారంభించబడింది'
//     },
//     label: {
//         en: 'Enquiry Creation Pending',
//         hi: 'जांच निर्माण लंबित',
//         kn: 'ವಿಚರಣೆ ಸೃಷ್ಟಿ ಬಾಕಿ',
//         ta: 'வினவல் உருவாக்கம் நிலுவையில் உள்ளது',
//         te: 'విచారణ సృష్టి పెండింగ్‌లో ఉంది'
//     },
//     order: 1,
//     color: 'info',
//     description: {
//         en: 'The customer has received the loan enquiry form on WhatsApp; awaiting form submission.',
//         hi: 'ग्राहक ने व्हाट्सएप प्रवाह प्राप्त कर लिया है। प्रवाह प्रस्तुत करने की प्रतीक्षा कर रहा है।',
//         kn: 'ಗ್ರಾಹಕನು ವಾಟ್ಸಾಪ್ ಫ್ಲೋ ಸ್ವೀಕರಿಸಿದ್ದಾರೆ. ಫ್ಲೋ ಸಲ್ಲಿಕೆಗೆ ನಿರೀಕ್ಷಿಸಲಾಗಿದೆ.',
//         ta: 'வாடிக்கையாளர் WhatsApp ஓட்டை பெற்றுள்ளார். ஓட்டை சமர்ப்பிக்க வேண்டும்.',
//         te: 'కస్టమర్ WhatsApp ఫ్లో స్వీకరించాడు. ఫ్లో సమర్పణ కోసం వేచి ఉంది.'
//     },
//     reminder: {
//         en: 'Hi, {}! We noticed that you have not completed the form flow. Please fill it out to proceed with your loan application for ',
//         hi: 'नमस्ते, {}! हमने देखा कि आपने फॉर्म प्रवाह को पूरा नहीं किया है। कृपया इसे भरें ताकि आप अपने ऋण आवेदन को आगे बढ़ा सकें।',
//         kn: 'ನಮಸ್ಕಾರ, {}! ನೀವು ಫಾರ್ಮ್ ಪ್ರಕ್ರಿಯೆಯನ್ನು ಪೂರ್ಣಗೊಳಿಸಿಲ್ಲವೆಂದು ನಾವು ಗಮನಿಸಿದ್ದೇವೆ. ದಯವಿಟ್ಟು ನಿಮ್ಮ ಸಾಲದ ಅರ್ಜಿಯನ್ನು ಮುಂದುವರಿಸಲು ಇದನ್ನು ಪೂರೈಸಿ.',
//         ta: 'வணக்கம், {}! நீங்கள் படிவ ஓட்டத்தை நிறைவு செய்யவில்லை என்று நாங்கள் கவனித்துள்ளோம். தயவுசெய்து உங்கள் கடன் விண்ணப்பத்தை தொடர அதை நிரப்புங்கள்.',
//         te: 'హలో, {}! మీరు ఫారమ్ ప్రవాహాన్ని పూర్తిచేయలేదని మేము గమనించాము. దయచేసి మీ రుణ దరఖాస్తును కొనసాగించడానికి దీన్ని పూరించండి.'
//     }
// }

// const createResellerAllStatus = (allStatus, initStatusKey) => {
//     const newAllStatus = structuredClone(allStatus);
//     // TODO: translations
//     newAllStatus[initStatusKey] = {
//         header: {
//             en: 'Enquiry Initiated',
//             // hi: 'चैट प्रारंभ',
//             // kn: 'ಚಾಟ್ ಆರಂಭಿಸಲಾಗಿದೆ',
//             // ta: 'உரையாடல் தொடங்கப்பட்டது',
//             // te: 'చాట్ ప్రారంభించబడింది'
//         },
//         label: {
//             en: 'Enquiry Creation Pending',
//             hi: 'जांच निर्माण लंबित',
//             kn: 'ವಿಚರಣೆ ಸೃಷ್ಟಿ ಬಾಕಿ',
//             ta: 'வினவல் உருவாக்கம் நிலுவையில் உள்ளது',
//             te: 'విచారణ సృష్టి పెండింగ్‌లో ఉంది'
//         },
//         order: 1,
//         color: 'info',
//         description: {
//             en: 'The customer has received the loan enquiry form on WhatsApp; awaiting form submission.',
//             hi: 'ग्राहक ने व्हाट्सएप प्रवाह प्राप्त कर लिया है। प्रवाह प्रस्तुत करने की प्रतीक्षा कर रहा है।',
//             kn: 'ಗ್ರಾಹಕನು ವಾಟ್ಸಾಪ್ ಫ್ಲೋ ಸ್ವೀಕರಿಸಿದ್ದಾರೆ. ಫ್ಲೋ ಸಲ್ಲಿಕೆಗೆ ನಿರೀಕ್ಷಿಸಲಾಗಿದೆ.',
//             ta: 'வாடிக்கையாளர் WhatsApp ஓட்டை பெற்றுள்ளார். ஓட்டை சமர்ப்பிக்க வேண்டும்.',
//             te: 'కస్టమర్ WhatsApp ఫ్లో స్వీకరించాడు. ఫ్లో సమర్పణ కోసం వేచి ఉంది.'
//         },
//         reminder: {
//             en: 'Hi, {}! We noticed that you have not completed the form flow. Please fill it out to proceed with your loan application for ',
//             hi: 'नमस्ते, {}! हमने देखा कि आपने फॉर्म प्रवाह को पूरा नहीं किया है। कृपया इसे भरें ताकि आप अपने ऋण आवेदन को आगे बढ़ा सकें।',
//             kn: 'ನಮಸ್ಕಾರ, {}! ನೀವು ಫಾರ್ಮ್ ಪ್ರಕ್ರಿಯೆಯನ್ನು ಪೂರ್ಣಗೊಳಿಸಿಲ್ಲವೆಂದು ನಾವು ಗಮನಿಸಿದ್ದೇವೆ. ದಯವಿಟ್ಟು ನಿಮ್ಮ ಸಾಲದ ಅರ್ಜಿಯನ್ನು ಮುಂದುವರಿಸಲು ಇದನ್ನು ಪೂರೈಸಿ.',
//             ta: 'வணக்கம், {}! நீங்கள் படிவ ஓட்டத்தை நிறைவு செய்யவில்லை என்று நாங்கள் கவனித்துள்ளோம். தயவுசெய்து உங்கள் கடன் விண்ணப்பத்தை தொடர அதை நிரப்புங்கள்.',
//             te: 'హలో, {}! మీరు ఫారమ్ ప్రవాహాన్ని పూర్తిచేయలేదని మేము గమనించాము. దయచేసి మీ రుణ దరఖాస్తును కొనసాగించడానికి దీన్ని పూరించండి.'
//         }
//     };
//     return {
//         OPTIMO01,
//         ...newAllStatus,
//     }
// };

// export const resellerStatusAllLoanTypes = {
//     "LAP": createResellerAllStatus(SBLAll, 'OPTLAP01'),
//     "UBL": createResellerAllStatus(UBLAll, 'OPTUBL01'),
//     "HL": createResellerAllStatus(SHLAll, 'OPTHL01'),
//     "PL": createResellerAllStatus(UPLAll, 'OPTPL01'),
// }
